export function resolveEmployeeIdFromProfile(me) {
  if (!me) return null;
  const id =
    me?.employee?.employee_id ??
    me?.employee_id ??
    (me?.legacy ? me?.employee?.employee_id : null);
  return id ? String(id) : null;
}

export function buildSelfEmployeeFromProfile(me) {
  if (!me) return null;
  const emp = me.employee || (me.legacy ? me.employee : null);
  if (!emp?.employee_id) return null;
  const name = [emp.first_name, emp.last_name].filter(Boolean).join(" ").trim();
  return {
    employee_id: emp.employee_id,
    first_name: emp.first_name,
    last_name: emp.last_name,
    employee_code: emp.employee_code,
    display_name: name || emp.email || "",
    email: emp.email,
  };
}

export function isEmployeeSelfService(user, role, principalType) {
  if (principalType === "employee") return true;
  return role === "employee" && Boolean(resolveEmployeeIdFromProfile(user));
}

/** Display label: "Name (CODE)" — matches Mark Attendance dropdown. */
export function formatEmployeeLabel(emp) {
  if (!emp) return "";
  const name =
    String(emp.display_name || "").trim() ||
    [emp.first_name, emp.last_name].filter(Boolean).join(" ").trim();
  const code = emp.employee_code ? ` (${emp.employee_code})` : "";
  return name ? `${name}${code}` : emp.employee_id || "";
}

export function findEmployeeById(employees, id) {
  if (!id || !Array.isArray(employees)) return null;
  return employees.find((e) => String(e.employee_id) === String(id)) || null;
}

export function sortEmployeesByLabel(employees) {
  return [...(employees || [])].sort((a, b) =>
    formatEmployeeLabel(a).localeCompare(formatEmployeeLabel(b), undefined, { sensitivity: "base" })
  );
}

/** YYYY-MM-DD from API date / ISO datetime. Empty string when missing. */
export function toDateOnlyString(value) {
  if (!value) return "";
  const m = String(value).match(/^(\d{4}-\d{2}-\d{2})/);
  return m ? m[1] : "";
}

/**
 * Employee can take attendance / payroll for a period when they overlapped it.
 * Join after period end → skip. Missing joining date → include.
 */
export function isEmployeeEligibleForPeriod(employee, fromDate, toDate) {
  if (!fromDate || !toDate) return true;
  const doj = toDateOnlyString(employee?.date_of_joining);
  if (doj && doj > toDate) return false;
  const dol = toDateOnlyString(employee?.date_of_leaving);
  if (dol && dol < fromDate) return false;
  return true;
}

export function filterEmployeesEligibleForPeriod(employees, fromDate, toDate) {
  return (employees || []).filter((emp) => isEmployeeEligibleForPeriod(emp, fromDate, toDate));
}

/** True when this calendar day is on or after the employee's joining date. */
export function isWorkDateOnOrAfterJoining(employee, workDate) {
  const doj = toDateOnlyString(employee?.date_of_joining);
  if (!doj || !workDate) return true;
  return String(workDate) >= doj;
}

/** Builds { value, label, sublabel, group, _raw } options for SearchableSelect. */
export function employeeSearchSelectOptions(employees) {
  return sortEmployeesByLabel(employees).map((emp) => {
    const code = emp.employee_code || "";
    const dept = emp.department?.name || emp.department_name || "";
    return {
      value: emp.employee_id,
      label: formatEmployeeLabel(emp) || "Unnamed",
      sublabel: [code, dept].filter(Boolean).join(" · "),
      group: dept || "All employees",
      _raw: emp,
    };
  });
}
